import React, { useMemo } from 'react';
import { useCountUp } from '../../../hooks';
import { Sparkline } from '../../atoms';
import styles from './StatCard.module.css';

export type StatCardVariant =
  | 'primary'
  | 'success'
  | 'warning'
  | 'danger'
  | 'neutral';
export type TrendDirection = 'up' | 'down' | 'neutral';

export interface StatCardProps {
  /**
   * The main metric label (e.g., "Total Revenue", "Active Users")
   */
  label: string;

  /**
   * The primary metric value to display
   */
  value: string | number;

  /**
   * Color variant for the icon badge
   */
  variant?: StatCardVariant;

  /**
   * Icon element to display in the badge (SVG or icon component)
   */
  icon?: React.ReactNode;

  /**
   * Trend direction indicator
   */
  trend?: TrendDirection;

  /**
   * Trend percentage or value (e.g., "12.5" for 12.5% or "324")
   */
  trendValue?: string | number;

  /**
   * Additional context text for the trend
   */
  trendLabel?: string;

  /**
   * Optional subtitle or description
   */
  subtitle?: string;

  /**
   * Click handler for the entire card
   */
  onClick?: () => void;

  /**
   * Additional CSS class name
   */
  className?: string;

  /**
   * Apply glassmorphism effect
   */
  glass?: boolean;

  /**
   * Make this stat card a prominent hero card (larger text and icon)
   */
  hero?: boolean;

  /**
   * Optional sparkline data to display trend visualization
   */
  sparklineData?: number[];

  /**
   * Custom value renderer (e.g., for CircularProgress)
   */
  customValueRenderer?: React.ReactNode;
}

/**
 * StatCard - A dashboard statistics card component with glassmorphism support
 *
 * Displays a key metric with an icon badge, trend indicator, and optional subtitle.
 * Perfect for dashboard overviews and KPI displays.
 *
 * @example
 * ```tsx
 * <StatCard
 *   label="Total Revenue"
 *   value="$124,500"
 *   variant="success"
 *   icon={<DollarIcon />}
 *   trend="up"
 *   trendValue="12.5"
 *   trendLabel="vs last month"
 *   glass
 * />
 * ```
 */
export const StatCard: React.FC<StatCardProps> = ({
  label,
  value,
  variant = 'neutral',
  icon,
  trend = 'neutral',
  trendValue,
  trendLabel,
  subtitle,
  onClick,
  className,
  glass = true,
  hero = false,
  sparklineData,
  customValueRenderer,
}) => {
  const cardClasses = [
    styles.card,
    glass && styles.glass,
    hero && styles.hero,
    onClick && styles.clickable,
    className,
  ]
    .filter(Boolean)
    .join(' ');

  const iconBadgeClasses = [
    styles.iconBadge,
    styles[`variant-${variant}`],
  ].join(' ');

  const trendClasses = [styles.trend, styles[`trend-${trend}`]].join(' ');

  // Parse and animate numeric values
  const { numericValue, suffix, prefix, shouldAnimate } = useMemo(() => {
    const valueStr = String(value);

    // Try to extract number with prefix/suffix (e.g., "$124", "94%", "142")
    const matches = valueStr.match(
      /^([^0-9]*)([0-9,]+(?:\.[0-9]+)?)([^0-9]*)$/
    );

    if (matches) {
      const prefix = matches[1];
      const numStr = matches[2].replace(/,/g, '');
      const suffix = matches[3];
      const num = parseFloat(numStr);

      return {
        numericValue: num,
        prefix,
        suffix,
        shouldAnimate: !isNaN(num),
      };
    }

    return {
      numericValue: 0,
      prefix: '',
      suffix: '',
      shouldAnimate: false,
    };
  }, [value]);

  // Animate the number if it's numeric
  const animatedValue = useCountUp(shouldAnimate ? numericValue : 0, 1500, 0);

  // Format the display value
  const displayValue = useMemo(() => {
    if (!shouldAnimate) {
      return value;
    }

    // Format with commas for large numbers
    const formattedNumber =
      animatedValue >= 1000
        ? animatedValue.toLocaleString('en-US')
        : animatedValue.toString();

    return `${prefix}${formattedNumber}${suffix}`;
  }, [shouldAnimate, animatedValue, prefix, suffix, value]);

  const renderTrendIcon = () => {
    if (trend === 'neutral') return null;

    if (trend === 'up') {
      return (
        <svg
          width="16"
          height="16"
          viewBox="0 0 16 16"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          className={styles.trendIcon}
          aria-hidden="true"
        >
          <path
            d="M12 5L8 9L6 7L2 11"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M12 9V5H8"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      );
    }

    return (
      <svg
        width="16"
        height="16"
        viewBox="0 0 16 16"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className={styles.trendIcon}
        aria-hidden="true"
      >
        <path
          d="M12 11L8 7L6 9L2 5"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M12 7V11H8"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    );
  };

  const content = (
    <>
      <div className={styles.header}>
        {icon && <div className={iconBadgeClasses}>{icon}</div>}
        <div className={styles.labelContainer}>
          <span className={styles.label}>{label}</span>
          {subtitle && <span className={styles.subtitle}>{subtitle}</span>}
        </div>
      </div>

      <div className={styles.body}>
        {customValueRenderer ? (
          <div className={styles.customValue}>{customValueRenderer}</div>
        ) : (
          <div className={styles.value}>{displayValue}</div>
        )}

        {(trend !== 'neutral' || trendValue) && (
          <div className={trendClasses}>
            {renderTrendIcon()}
            {trendValue && (
              <span className={styles.trendValue}>
                {typeof trendValue === 'number' ? `${trendValue}%` : trendValue}
              </span>
            )}
            {trendLabel && (
              <span className={styles.trendLabel}>{trendLabel}</span>
            )}
          </div>
        )}

        {sparklineData && sparklineData.length > 0 && (
          <div className={styles.sparklineContainer}>
            <Sparkline
              data={sparklineData}
              color={
                trend === 'up'
                  ? 'var(--color-success)'
                  : trend === 'down'
                    ? 'var(--color-danger)'
                    : 'var(--color-primary)'
              }
              width={hero ? 160 : 120}
              height={hero ? 50 : 40}
              showDot
              showFill
            />
          </div>
        )}
      </div>
    </>
  );

  if (onClick) {
    return (
      <button
        className={cardClasses}
        onClick={onClick}
        type="button"
        aria-label={`${label}: ${value}`}
      >
        {content}
      </button>
    );
  }

  return (
    <div
      className={cardClasses}
      role="article"
      aria-label={`${label}: ${value}`}
    >
      {content}
    </div>
  );
};
